/**
 * Auction houses that must never surface on a collection page. Supplied by merch (LAAC-2376);
 * kept sorted ascending by id so diffs stay readable when the list is revised, with the house
 * name alongside each id so a reviewer can tell what is being excluded without a DB lookup.
 *
 * Fed into the search-party query as `options.auctionHouse[0].exclude` -- see
 * `getAugmentedSearchOptions` in `@/redux/modules/search/search.helpers`.
 *
 * Test houses (Acme and friends) are deliberately absent: Dewey never indexes them in the first
 * place. Every lot query in `Dewey/internal/stores/LiveAuctStore.go` joins auction houses with
 * `h.houseID NOT IN (SELECT houseId from test_sellers)`, so they cannot reach search-party and
 * listing them here would be dead weight.
 *
 * Not covered here: the ticket also asks to exclude all *retail* houses. That is handled
 * separately, and no longer from this list -- search-party#483 added an optional `retail` boolean
 * to v4 lot search, so `buildSearchQuery` sends `retail: false` for the collection layout. Keep
 * this list for the named houses, which are excluded for reasons unrelated to the retail flag.
 */
export const EXCLUDED_AUCTION_HOUSE_IDS: number[] = [
    306, // Clars Auctions
    361, // Bill Hood & Sons Art & Antique Auctions
    413, // Kodner Galleries Inc.
    421, // John Nicholson Auctioneers
    989, // Seized Assets Auctioneers
    1008, // Nazmiyal Auctions
    1096, // Art & Jewelry Auction House
    1103, // Artemis Fine Arts
    1162, // 888 Auctions
    1327, // Preston Hall Gallery
    1436, // China Arts Auction
    1452, // Pydern Auctions
    1497, // Altair Auctions
    1666, // KaiPing JuBao Web
    1998, // William's Auctions, LLC
    2120, // Linwoods Auction
    2171, // Scottsdale Art Exchange
    2372, // Newport Beach Auction Company
    2431, // Omega Auction Corp.
    2514, // Jacobs Auctions / Greenwich Auctions
    2669, // Richard Stedman Estate Services LLC
    2674, // Wilson Fine Art & Antiques
    2713, // Essex Auction and Estate Services
    2801, // Mega International Auction
    2877, // Auction Kings Gallery
    3169, // Fine Art Online Auctions, LLC
    3173, // Wholesale Art Auctions LLC
    3209, // Find Treasure
    3393, // Art Everywhere Auctions Inc.
    3414, // Buy Art Auctions, Inc.
    3526, // Andrew Auctionz Inc.
    3721, // Activity Auctions
    3746, // Royal Treasures Warehouse LLC
    3780, // Modern Jewelry
    5273, // Auctions by Terry
    5277, // Auctions Online
    5291, // USA Live Auctions
    5542, // Antique Auctions Plus
    5548, // Dynasty Fine Arts
    5582, // International Art Auction Gallery
    5752, // Art Antique Auction Gallery
    5789, // Jumbo Auction House
    5790, // Art Legacy Auctions
    5807, // Finger Lakes Estate & Auction Company
    5851, // Fantastic Auctions
    5891, // Willow Fine Art Gallery
    5934, // Broward Auction Gallery
    5952, // Ukiyoe Gallery Japanese Woodblock Prints
    6003, // Time Auctions
    6019, // West Coast Auction Gallery
    6024, // Robinhood Auctions
    6038, // V.N. Collectible
    6056, // Rapid Estate Liquidators and Auction Gallery
    6100, // Federal Assets Auctioneers
    6143, // Star Auction LLC
    6177, // NY Elizabeth
    6190, // Estate Collection Auctioneer
    6226, // Gems Auction
    6247, // Compendium Auction House
    6261, // Helmuth Stone Gallery
    6272, // Mega Oriental Arts Auction LLC
    6312, // Marnie Collection Auction
    6343, // Coral Gables Auction
    6374, // USA Antiques
    6450, // MJ Auction Gallery LLC
    6469, // Golden Moment Auctions
    6523, // White Knight Auction
    6542, // Treasure of Gems & Jewelry
    6628, // Weatherham Estate Treasures
    6662, // Art Online LLC
    6667, // Dalshire International
    6801, // Bidhaus
    6824, // Private NY Collection
    6975, // Antique Arena Inc.
    6985, // Amazing Collectible Galleries
    7102, // Annzstiques Auction House
    7146, // AWS Auctioneers
    7178, // MAKARE 68
    7252, // Yakon Auctions
    7261, // Gold Standard Auctions
    7335, // Latair
    7378, // YaShawn Auction House
    7382, // Walnut Tree Auction House, Inc.
    7405, // Irvington Auction Inc
    7473, // ATR AUCTIONS
    7498, // Fine Arts Italy
    7571, // Global Networth Auctions
    7578, // Azaya Art Gallery
    7597, // Castle Valley Art Gallery Inc.
    7625, // Zipriani Auction House
    7627, // Lima Art Gallery
    7634, // Eldred's
    7684, // Michael's Gallery
    7718, // JIFB Fine Art
    7778, // Giorgio Auctions
    7785, // WeeArt Gallery Inc.
    7804, // Mynt Auctions
    7847, // Colonial Auctions Trujillo
    7963, // Eastern Art Auction House
    7996, // Summer Auction
    8046, // Art Atelier
    8101, // ABC Gallery
    8115, // Alden's Auction
    8132, // Hotspot Auctions
    8143, // UPS Auction
    8156, // JVG Auctions
    8237, // Gordon Fine Art Gallery LLC
    8343, // Swanley Auction LTD
    8367, // WOA AUCTION GALLERY
    8487, // International Fine Art Gallery
    8556, // Vintage Art Finder Gallery
    8570, // The Art's World
    8580, // International Art Auction Galleries
    8621, // Montana Antiques (related to Dynasty Heritage 10405)
    8657, // 5 Continents Art
    8665, // Eduardo Monet Gallery
    8770, // A&D Art Limited
    8777, // Williamson Auction Gallery
    8811, // JIDA ART AUCTIONS
    8816, // Drake Gallery Inc.
    8846, // Retro Roadshow Auctions
    8879, // Vintissimo Auctions
    9289, // Florida Highwaymen Auctions LLC
    9318, // Deluxe Auction
    9391, // SEUYCO
    9621, // Lucerne Auctions LLC
    9814, // Hartington Gallery
    9901, // The East is Red Auctions
    10099, // Lume Arts Inc
    10293, // Greenville Estate Sales
    10302, // Crown & Gavel LLC
    10373, // Nile Jewels Co Ltd
    10405, // Dynasty Heritage Auctions (related to Montana Antiques 8621)
    10411, // Park Place Holdings
    10520, // ABC Rugs Kilims, Inc.
];
